import React, {
  useMemo,
} from 'react';
import styled from 'styled-components';
import 'react-image-lightbox/style.css';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { Booth, DesignElement } from 'models/cms/booths';
import {
  FETCH_ASSETS_URL,
} from 'appenv';

import { ReactComponent as ColourChange } from 'assets/booths/923-customization/option2/colourchange.svg';

import BusinessPopover from 'pages/booths/components/BusinessPopover';
import BoothContainer from '../components/BoothContainer';
import GenericCMSBoothBanner from '../components/GenericCMSBoothBanner';
import useEmailContactsMemo from '../useEmailContactsMemo';

const BoothBackgroundImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option2/bg.png`;
const LightImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option2/lighting-effect.png`;

const CounterImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option2/counter.png`;

const SofaImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option2/sofa.png`;

const RepresentativeImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option2/representative.png`;

const BackgroundImage = styled.div`
  height: 110%;
  width: 130%;
  position: absolute;
  left: -150px;
  background: url(${BoothBackgroundImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const OverheadBannerContainer = styled.div`
  position: absolute;
  width: 300px;
  height: 160px;
  left: calc((100% - 261px) / 2);
  top: 9px;
  z-index: 2;
`;

const LightBox = styled.img`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 650px;
  top: 87px;
  left: -16px;
  z-index: 1;
  pointer-events: none;
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 20px;
`;

const BackdropBannerContainer = styled.div`
  height: 330px;
  width: 330px;
  position: absolute;
  bottom: -5px;
  left: 55px;
`;

const PullUpBannerContainer = styled.div`
  height: 265px;
  width: 135px;
  position: absolute;
  bottom: -38px;
  right: -54px;
  z-index: 2;
`;

const TvBannerContainer = styled.div`
  height: 150px;
  top: 346px;
  left: 431px;
  width: 268px;
  position: absolute;
  z-index: 2;
`;

const TvBackgroundContainer = styled.div`
  height: 150px;
  top: 346px;
  left: 431px;
  width: 268px;
  position: absolute;
  background: #fff;
`;

const CounterBannerContainer = styled.div`
  height: 135px;
  bottom: -52px;
  right: 107px;
  width: 129px;
  position: absolute;
  z-index: 3;
`;

const CounterBackground = styled.div`
  background: url(${CounterImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 165px;
  bottom: -60px;
  right: 53px;
  width: 240px;
  z-index: 2;
`;

const BusinessPopoverContainer = styled.div`
  position: absolute;
  top: -20px;
  right: -150px;
  width: 340px;
`;

const SofaContainer = styled.div`
  background: url(${SofaImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  height: 1000px;
  width: 1000px;
  top: -22px;
  left: 0;
  pointer-events: none;
  z-index: 3;
`;

const RepresentativeContainer = styled.div`
  background: url(${RepresentativeImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  width: 90px;
  height: 200px;
  right: 105px;
  bottom: 55px;
`;

const BackgroundColorContainer = styled(ColourChange)`
  height: 600px;
  width: 745px;
  position: absolute;
  left: 17px;
  bottom: -100px;
  fill: ${({ color }) => color || '#f6f6f6'};
`;

const TvBackdropColorContainer = styled.div`
  height: 290px;
  top: 320px;
  left: 404px;
  width: 337px;
  position: absolute;
  background: ${({ color }) => color || '#f6f6f6'};
  opacity: .3;
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string, isCMSElement: boolean) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const locale = useSelector(selectLocale);
  const backgroundColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'background_color')?.color, [booth.designElements]);
  const tvBackdropColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'tv_backdrop_color')?.color, [booth.designElements]);
  const overheadBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'overhead_banner'), [booth.designElements]);
  const backdrop: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'backdrop'), [booth.designElements]);
  const counter: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'counter'), [booth.designElements]);
  const pullUpBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'pull_up_banner'), [booth.designElements]);
  const tv: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode.name === 'tv'), [booth.designElements]);
  const tabs = booth?.rootContentElement?.tabs || [];
  const emailContacts = useEmailContactsMemo(tabs);

  return (
    <BoothContainer needOffset={!isPreview}>
      <BackgroundContainer>
        <BackgroundImage />
      </BackgroundContainer>
      <BackgroundColorContainer color={backgroundColor} />
      <TvBackdropColorContainer color={tvBackdropColor} />
      <TvBackgroundContainer />
      <BusinessPopoverContainer>
        <BusinessPopover
          boothId={booth.id}
          boothName={booth.name[locale]}
          emailContacts={emailContacts}
          meetupUrl={booth.meetupEnabled ? booth.appointmentBookingUrl : ''}
          livestreams={booth.livestreams}
          videoCallUrl={booth.videoCallUrl}
        />
      </BusinessPopoverContainer>
      <OverheadBannerContainer onClick={() => handleClick(overheadBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={overheadBanner}
          onBannerClick={handleClick as any}
        />
      </OverheadBannerContainer>
      <LightBox src={LightImage} />
      <BackdropBannerContainer onClick={() => handleClick(backdrop.name, true)}>
        <GenericCMSBoothBanner
          bannerData={backdrop}
          onBannerClick={handleClick as any}
        />
      </BackdropBannerContainer>
      <PullUpBannerContainer onClick={() => handleClick(pullUpBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={pullUpBanner}
          onBannerClick={handleClick as any}
        />
      </PullUpBannerContainer>
      <TvBannerContainer onClick={() => handleClick(tv.name, true)}>
        <GenericCMSBoothBanner
          autoPlayVideo={!isPreview}
          bannerData={tv}
          onBannerClick={handleClick as any}
        />
      </TvBannerContainer>
      <CounterBackground />
      <CounterBannerContainer onClick={() => handleClick(counter.name, true)}>
        <GenericCMSBoothBanner
          bannerData={counter}
          onBannerClick={handleClick as any}
        />
      </CounterBannerContainer>
      <SofaContainer />
      {!isPreview && (
        <RepresentativeContainer />
      )}
    </BoothContainer>
  );
};

export default Banner;
